// Scope: renders one acknowledged direct conversation with a session-persisted draft.

import React, { useRef, useState } from "react";
import ScrollToBottom from "react-scroll-to-bottom";
import useChatRoomStore from "../../chatRoomStore";

const formatMessageTime = (value) => new Date(value || Date.now()).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
});

const Chat = ({ myChat, socket, chatId, onClose, connectionState, typingText, unavailable, onReconnect }) => {
    const chatPartner = myChat?.name || "";
    const { directMessages, appendDirectMessage, drafts, setDraft } = useChatRoomStore();
    const messages = myChat ? directMessages[myChat.id] || [] : [];
    const workspace = myChat ? `direct:${myChat.id}` : "direct:none";
    const messageInput = drafts[workspace] || "";
    const [sending, setSending] = useState(false);
    const typingTimerRef = useRef();

    if (!myChat) return null;

    const handleSendMessage = () => {
        const message = messageInput.trim();
        if (!message || !socket?.connected || sending) return;

        setSending(true);
        socket.emit("sendMessage", { receiver: myChat.id, message }, (result) => {
            setSending(false);
            if (!result?.ok || !result.message) return;
            appendDirectMessage(myChat.id, result.message);
            setDraft(workspace, "");
        });
    };

    const handleExitChat = () => {
        socket.emit("chatLeft", { partnerId: myChat.id });
        onClose(myChat.id);
    };

    return (
        <div className="active-chat-container">
            <div className="chat-header">
                <div>
                    <h2>{chatPartner}</h2>
                    <span className="chat-header__type">Direct conversation</span>
                </div>
                <button onClick={handleExitChat} className="exit-btn">Close</button>
            </div>

            {connectionState !== "connected" && (
                <div className="conversation-connection-state" role="status">
                    Reconnecting… Your conversation and draft are preserved.
                </div>
            )}
            {unavailable && (
                <div className="conversation-connection-state unavailable" role="status">
                    This person disconnected. Your history is preserved.
                    <button type="button" onClick={onReconnect}>Request to reconnect</button>
                </div>
            )}

            <ScrollToBottom className="chat-messages" mode="bottom" behavior="smooth">
                <div className="messages">
                    {messages.map((message, index) => {
                        const senderId = message.sender?.id ?? message.sender;
                        const isMine = String(senderId) === String(chatId);
                        return (
                            <div
                                key={message.id || `${senderId}-${message.timeStamp || index}`}
                                className={`group-messages ${isMine ? "mine" : "others"}`}
                            >
                                <div className="message-meta">
                                    <span className="message-author">{isMine ? "You" : chatPartner}</span>
                                    <time>{formatMessageTime(message.timeStamp)}</time>
                                </div>
                                <div className={isMine ? "my-message" : "other-message"}>{message.message}</div>
                            </div>
                        );
                    })}
                </div>
            </ScrollToBottom>
            <div className="typing-indicator" aria-live="polite">{typingText || "\u00a0"}</div>

            <div className="chat-input">
                <textarea
                    placeholder={`Message ${chatPartner}`}
                    rows="2"
                    value={messageInput}
                    maxLength={1000}
                    aria-label={`Message ${chatPartner}`}
                    onChange={(event) => {
                        setDraft(workspace, event.target.value);
                        socket?.emit("directTyping", { receiver: myChat.id, isTyping: true });
                        window.clearTimeout(typingTimerRef.current);
                        typingTimerRef.current = window.setTimeout(() => {
                            socket?.emit("directTyping", { receiver: myChat.id, isTyping: false });
                        }, 1_200);
                    }}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                            event.preventDefault();
                            handleSendMessage();
                        }
                    }}
                />
                <div className="chat-compose-actions">
                    <span className="chat-character-count" aria-live="polite">{messageInput.length}/1000</span>
                    <button
                        onClick={handleSendMessage}
                        className="send-btn"
                        disabled={unavailable || connectionState !== "connected" || sending || !messageInput.trim()}
                    >
                        {sending ? "Sending…" : "Send"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Chat;
